import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Sparkles } from 'lucide-react';
import { Personagem, PersonagemFormData } from '../../../types/personagem';
import { PersonagemPhotoPicker } from './PersonagemPhotoPicker';

interface PersonagemFormModalProps {
  isOpen: boolean;
  personagem: Personagem | null;
  saving: boolean;
  onClose: () => void;
  onSave: (data: PersonagemFormData) => void;
}

const emptyForm: PersonagemFormData = {
  nome: '',
  categoria: '',
  descricao: '',
  precoBase: '',
  ativo: true,
  foto: null
};

export const PersonagemFormModal: React.FC<PersonagemFormModalProps> = ({
  isOpen,
  personagem,
  saving,
  onClose,
  onSave
}) => {
  const [form, setForm] = useState<PersonagemFormData>(emptyForm);
  const [preview, setPreview] = useState<string>('');

  useEffect(() => {
    if (!isOpen) return;
    if (personagem) {
      setForm({
        nome: personagem.nome,
        categoria: personagem.categoria || '',
        descricao: personagem.descricao || '',
        precoBase: personagem.precoBase || '',
        ativo: personagem.ativo,
        foto: null
      });
      setPreview(personagem.fotoUrl || '');
    } else {
      setForm(emptyForm);
      setPreview('');
    }
  }, [isOpen, personagem]);

  if (!isOpen) return null;

  const isEditing = Boolean(personagem);

  const handlePhotoReady = (file: File, previewUrl: string) => {
    setForm((prev) => ({ ...prev, foto: file }));
    setPreview(previewUrl);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.nome.trim()) return;
    onSave(form);
  };

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-[fadeIn_0.1s_ease-out_forwards]">
      <div
        className="bg-white shadow-lg border border-slate-200 rounded-lg overflow-hidden max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto animate-[slideInFromBottom2_0.15s_ease-out_forwards] animate-[zoomIn95_0.15s_ease-out_forwards]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header do Modal */}
        <div className="relative bg-gradient-to-r from-teal-500 to-teal-600 text-white p-4">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-white/30"></div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  {isEditing ? 'Editar Personagem' : 'Novo Personagem'}
                </h2>
                <p className="text-sm text-teal-100">Preencha os dados do personagem</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Formulário */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <PersonagemPhotoPicker previewUrl={preview} onPhotoReady={handlePhotoReady} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">Nome do Personagem *</label>
              <input
                type="text"
                required
                value={form.nome}
                onChange={(e) => setForm((prev) => ({ ...prev, nome: e.target.value }))}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                placeholder="Ex: Mickey Mouse"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Categoria</label>
              <input
                type="text"
                value={form.categoria}
                onChange={(e) => setForm((prev) => ({ ...prev, categoria: e.target.value }))}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                placeholder="Ex: Turma do Mickey"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Preço Base (R$)</label>
              <input
                type="text"
                value={form.precoBase}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, precoBase: e.target.value.replace(/[^0-9,]/g, '') }))
                }
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                placeholder="Ex: 300,00"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">Descrição</label>
              <textarea
                rows={3}
                value={form.descricao}
                onChange={(e) => setForm((prev) => ({ ...prev, descricao: e.target.value }))}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                placeholder="Observações sobre o personagem, figurino, tempo de apresentação, etc."
              />
            </div>

            <div className="md:col-span-2">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={form.ativo}
                  onChange={(e) => setForm((prev) => ({ ...prev, ativo: e.target.checked }))}
                  className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                />
                Disponível para orçamentos
              </label>
            </div>
          </div>

          {/* Botões de Ação */}
          <div className="flex justify-end gap-4 pt-6 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors font-medium"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg hover:from-teal-600 hover:to-teal-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
            >
              {saving ? 'Salvando...' : isEditing ? 'Salvar Alterações' : 'Adicionar Personagem'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};
